export const QUALIFICATION_CODE = {
  GENDER: 211,
  AGE: 212,
  INCOME: 213,
  RACE: 214,
  EMPLOYMENT: 215,
  EDUCATION: 216,
  CHILDREN_LEGACY: 218,
  DEVICE: 219,
  ZIPCODE: 229,
  CHILD_AGE_GENDER: 232,
  FSA: 303,
} as const;

export const FILE_UPLOAD_LOCATION_QUALIFICATION_CODES: ReadonlySet<number> =
  new Set([QUALIFICATION_CODE.ZIPCODE, QUALIFICATION_CODE.FSA]);

export const ZIPCODE_DISABLED_COUNTRY_CODES: ReadonlySet<string> = new Set([
  'KZ',
  'VN',
]);

export const LOCATION_UPLOAD_ROW_LIMIT = 25001;

export const FSA_COUNTRY_CODE = 'CA';

export const LOCATION_UPLOAD_ACCEPTED_EXTENSION = '.csv';

// Decipher's own numeric age-range unit codes — the wire's range_sets[].units field for an age
// criterion isn't a free-form scale factor, it's one of these two fixed codes. Mirrors
// libs/web/buyers/data-access/src/decipher-mapping.constants.ts's AGE_YEAR (same underlying
// platform convention, reused here since quota criteria write this same wire field).
export const AGE_RANGE_UNIT_CODE = {
  YEAR: 311,
  MONTH: 312,
} as const;

// Decipher's own numeric unit code for an Income range qualification's wire value.
export const INCOME_RANGE_UNIT_CODE = 321;

export const RANGE_QUALIFICATION_CODES: ReadonlySet<number> = new Set([
  QUALIFICATION_CODE.AGE,
  QUALIFICATION_CODE.INCOME,
]);

// A plain range qualification's unit is implicit and fixed per qualification, never a
// buyer-selectable toggle — mirrors legacy CommonService.getUnit() verbatim: Income is always
// 321, Age is always 311 (years). Shared by both the qualification's own committed range value
// (build-updated-survey-qualification.helper.ts / build-default-survey-qualification.helper.ts)
// and Quota Manager's plain-range criteria, so the two can never disagree. Only meaningful for
// PLAIN_RANGE_QUALIFICATION_CODES — callers must gate on that set first.
export function rangeUnitsForQualification(qualificationCode: number): number {
  return qualificationCode === QUALIFICATION_CODE.INCOME
    ? INCOME_RANGE_UNIT_CODE
    : AGE_RANGE_UNIT_CODE.YEAR;
}

export const DEFAULT_AGE_QUOTA_CRITERIA = {
  NAME: 'Age',
  MIN: 18,
  MAX: 99,
  UNITS: AGE_RANGE_UNIT_CODE.YEAR,
} as const;

export const QUALIFICATIONS_RENDER_TYPE = {
  MULTIPUNCH_DROPDOWN: 1,
  FILE_UPLOAD: 2,
  SEARCH: 3,
  RANGE: 4,
  MULTIPUNCH_LIST: 5,
} as const;

export const NOT_TO_BE_GROUPED_QUALIFICATIONS: Set<number> = new Set([
  211, 212, 213, 218, 229, 232, 303,
]);

// Real backend condition/unit codes behind qualification 218's 3-qualification structure
// (218 itself, 220 CHILD_GENDER, 230 CHILD_AGE) — pinned against purespectrum-lib@1.3.7's
// child-age-gender-mapper/quota-mapper.constants.ts, which the package doesn't re-export.
export const CHILDREN_LEGACY_CONDITION_CODE = { HAVE: 112, NONE: 111 } as const;
export const CHILD_GENDER_CONDITION_CODE = { BOY: 111, GIRL: 112 } as const;
export const CHILD_AGE_UNIT_CODE = { YEARS: 311, MONTHS: 312 } as const;

// Every location qualification (State/CSA/CBSA/County/DMA) is presented under the one generic
// "Location" label, so the name alone cannot say which one a payload means — see
// StandardSurveyPayloadMapper, which sends the key for these instead.
export const LOCATION_QUALIFICATION_NAME = 'Location';

export const CHILD_GENDER_QUALIFICATION_NAME = 'Child Gender';
// The real 218/232 qualification's own name — carried by both a have-children and a no-children
// quota row alike (see isHaveChildrenCriterion for how the two are told apart instead), and by
// the outbound 232 criterion's own qualification_name.
export const CHILDREN_QUALIFICATION_NAME = 'Children';
